import React from 'react'
import type { ConfigGlobalModel } from '../../defs/defs'
import { PageErrorNoRouting } from '../../pages/PageError'
import EqxLoading from '../../shared/components/EqxLoading'
import { useTenantlessApiContext } from './AppTenantlessApi'

function normalizeConfigGlobal(raw: unknown): ConfigGlobalModel {
    const r = raw as Record<string, unknown>
    return {
        apiVersion: String(r.apiVersion ?? r.APIVersion ?? r.version ?? r.Version ?? ''),
        minApiVersion: String(r.minApiVersion ?? r.MinAPIVersion ?? ''),
        aadClientId: String(r.aadClientId ?? r.aadClientID ?? r.AADClientID ?? ''),
    }
}

const GlobalConfigContext = React.createContext<ConfigGlobalModel | undefined>(undefined)

export const useGlobalAppConfigContext = (): ConfigGlobalModel => {
    const v = React.useContext(GlobalConfigContext)
    if (v === undefined) {
        throw new Error('useGlobalAppConfigContext must be used within AppGlobalConfigProvider')
    }
    return v
}

export const AppGlobalConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const api = useTenantlessApiContext()
    const [data, setData] = React.useState<ConfigGlobalModel | null>(null)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        let cancelled = false
        api.endpoints
            .configGlobal()
            .execute(api)
            .then((res) => {
                if (!cancelled) setData(normalizeConfigGlobal(res))
            })
            .catch((err) => {
                console.error(err)
                if (!cancelled) {
                    setError('Unable to load application configuration. Please try again later.')
                }
            })
        return () => {
            cancelled = true
        }
    }, [api])

    if (error) {
        return <PageErrorNoRouting error={error} />
    }

    if (!data) {
        return <EqxLoading message="Loading..." />
    }

    if (!data.apiVersion?.trim() || !data.aadClientId?.trim()) {
        return (
            <PageErrorNoRouting error="Application configuration from the server is incomplete. Contact support." />
        )
    }

    return <GlobalConfigContext.Provider value={data}>{children}</GlobalConfigContext.Provider>
}
